import { axialHexDistance } from '@/game/hexmap';
import {
  buildingLevel,
  buildingMaxHp,
  buildingRange,
  buildingStrength,
  canCancel,
  cancelRefund,
  cityBonuses,
  cityWorkerCap,
  fromMilli,
  levelStat,
  outsideTerritory,
  storageCapacity,
  territoryOwners,
  watchStats,
} from '@/game/sim';
import { assignedWorkers } from '@/game/view/activity';
import {
  buildingApplied,
  buildingOutput,
  buildingRoster,
  buildingTask,
  cityEffect,
  cityOf,
  rateText,
  upgradingTask,
  workPlace,
} from '@/game/view/buildingInfo';
import { buildingIcon } from '@/game/view/buildingsList';
import { cityName } from '@/game/view/cities';
import { assignIdleCommand, releaseCommand } from '@/game/view/cityWorkers';
import { constructionStatus, formatDuration } from '@/game/view/construction';
import { roomToGrow } from '@/game/view/growth';
import { costParts } from '@/game/view/resources';
import { tickRoster, tickWorkBoard, tickWorkers } from '@/game/view/tickCache';
import { levelPreview } from '@/game/view/upgradePreview';
import { upgradeProgress } from '@/game/view/upgradeProgress';
import { LevelAction, LevelCard, LevelChip, type LevelSectionProps } from './CityPanel';
import { Cost } from './Cost';
import { DockPanel } from './hud/DockPanel';
import { InfoPanel, SectionHead } from './InfoPanel';
import { Stat } from './UnitCard';

interface BuildingPanelProps extends LevelSectionProps {
  onClose: () => void;
  /** A watchtower's Set watch spot: enter the targeting mode for it. */
  onSetWatch?: (buildingId: string) => void;
}

/**
 * Selected own building other than a city (farm, forestry, barracks), on the
 * shared info panel (`InfoPanel`, mockup 02 B): header (badge, name, level,
 * city, hp), a task line (Working / Idle: no workers / storing / Upgrading to level N), the output
 * of a resource building now against its maximum, its worker roster
 * (add the nearest idle worker, release a pinned one: #102's commands), what is applied and the next level; an unfinished site shows
 * its progress and a Cancel button instead.
 */
export function BuildingPanel(props: BuildingPanelProps) {
  if (props.building.buildProgress < 1) return <SitePanel {...props} />;
  return <CompletedPanel {...props} />;
}

function CompletedPanel({
  state,
  building,
  playerId,
  rules,
  terrain,
  dispatch,
  cityNumbers,
  names,
  onHighlight,
  onClose,
  onSetWatch,
}: BuildingPanelProps) {
  const ctx = { terrain, rules };
  const def = rules.buildings[building.type];
  const levelPrev = levelPreview(state, ctx, playerId, building, cityNumbers, names);
  const cityHp = def ? (levelStat(def, buildingLevel(building), 'cityHp') ?? 0) : 0;
  const cityStrength = def ? (levelStat(def, buildingLevel(building), 'cityStrength') ?? 0) : 0;
  const cityVision = def ? (levelStat(def, buildingLevel(building), 'cityVision') ?? 0) : 0;
  // A watchtower: its reach, the sight around its spot, and the spot itself.
  const watch = watchStats(rules, building);
  const spotDistance = building.watch ? axialHexDistance(building.pos, building.watch) : null;
  const unlocks = Object.values(rules.units)
    .filter((u) => u.requires?.building === building.type)
    .map((u) => `${u.name} (level ${u.requires?.level})`);
  const capacity = storageCapacity(rules, building);
  const storedCount = Object.values(state.units).filter((u) => u.inside === building.id).length;
  const tiles = 1 + (building.footprint?.length ?? 0);
  const growth = roomToGrow(state, ctx, building);
  const outside = outsideTerritory(state, rules, building);
  // Everyone working it, inside or on the road hauling, as its 👷 badge counts them.
  const workers = assignedWorkers(state, rules, building, tickWorkers(state, rules));
  // A building that fires (the missile silo) keeps firing wherever it stands.
  const fire = buildingStrength(rules, building, cityBonuses(state, rules));
  const fireRange = buildingRange(rules, building);
  const output = buildingOutput(state, ctx, building);
  const roster = output ? buildingRoster(state, ctx, building, cityNumbers ?? new Map()) : [];
  const city = cityOf(state, rules, building);
  const cityNo = cityNumbers ?? new Map<string, number>();
  const place =
    output && city && city.buildProgress >= 1
      ? workPlace(
          {
            state,
            ctx,
            city,
            owners: territoryOwners(state, rules),
            ...tickWorkBoard(state, ctx, city.id),
            idleIds: tickRoster(state, { ...ctx, playerId }).idle,
            resources: [output.resource],
            cityNumbers: cityNo,
            cap: cityWorkerCap(rules, city),
          },
          building.id
        )
      : null;
  const pinned = new Set(place?.chips.filter((c) => c.kind === 'pinned').map((c) => c.unitId));
  // The add button always shows; its reason sits beside it.
  const add = place?.addIdle;
  const addUnit = add && 'unitId' in add ? add.unitId : null;
  const addReason = add
    ? 'reason' in add
      ? add.reason
      : null
    : output
      ? outside
        ? 'Outside your territory'
        : 'No finished city to send a worker from'
      : null;
  const reasonId = `add-reason-${building.id}`;
  const effect = output ? cityEffect(state, ctx, building, cityNumbers ?? new Map()) : null;
  const progress = upgradeProgress(state, rules, building, playerId);
  const upgrade = progress ? upgradingTask(rules, building, progress) : null;
  const assigned = roster.length;
  const task = buildingTask(state, rules, building, {
    working: output?.workers ?? 0,
    assigned,
    hauling: workers.hauling,
    full: workers.full,
  });
  const hp = {
    hp: Math.ceil(fromMilli(building.hp)),
    max: Math.ceil(fromMilli(buildingMaxHp(state, rules, building))),
  };
  const lost = output ? output.max - output.now : 0;

  return (
    <InfoPanel
      kind="building"
      badge={buildingIcon(building.type)}
      name={def?.name ?? building.type}
      level={<LevelChip rules={rules} building={building} />}
      chips={
        <>
          <span className="chip shrink-0 tabular-nums">
            {hp.hp}/{hp.max} hp
          </span>
          {city && (
            <span className="chip shrink-0">
              {cityName(city, playerId, cityNumbers ?? new Map())}
            </span>
          )}
          {fire > 0 ? (
            <span className="chip shrink-0 tabular-nums">
              fires {fire}/s{fireRange > 1 ? ` · ${fireRange} tiles` : ''}
            </span>
          ) : null}
        </>
      }
      hp={hp}
      task={
        upgrade
          ? { tone: upgrade.tone, text: upgrade.verb }
          : {
              tone: task.tone,
              text: output
                ? `${task.verb} · working ${output.workers}/${output.slots} · assigned ${assigned}`
                : task.verb,
            }
      }
      onClose={onClose}
      stats={
        <>
          {outside ? (
            <p className="text-warn">
              {fire > 0
                ? 'Outside your territory: it keeps firing'
                : 'Outside your territory: it produces nothing and takes no workers until your border covers it again'}
            </p>
          ) : null}
          {output ? (
            <>
              <div className="grid grid-cols-3 gap-1">
                <Stat label={`${rules.resources[output.resource]?.name ?? output.resource} now`}>
                  {rateText(output.resource, output.now)}
                </Stat>
                <Stat label="Rate">{output.rateMultiplier}×</Stat>
                <Stat label="Working">
                  {output.workers}/{output.slots} slots
                </Stat>
                <Stat label="City bonus">
                  {output.cityBonus > 0
                    ? `+${Number((output.cityBonus * 100).toFixed(1))}%${output.cityTitle ? ` ${output.cityTitle}` : ''}`
                    : 'none'}
                </Stat>
                <Stat label="Max output">{rateText(output.resource, output.max)}</Stat>
                <Stat label="Tiles">{tiles}</Stat>
              </div>
              {lost > 0.005 && !outside ? (
                <p className="text-fg-muted">
                  {output.slots - output.workers > 0
                    ? `${output.slots - output.workers} ${output.slots - output.workers === 1 ? 'slot is' : 'slots are'} empty: `
                    : 'Full loads waiting: '}
                  <span className="tabular-nums">{rateText(output.resource, lost)}</span> lost
                </p>
              ) : null}
              {workers.full > 0 && !outside ? (
                <p className="text-warn">
                  {workers.full} {workers.full === 1 ? 'has' : 'have'} a full load and can't gather:
                  move {workers.full === 1 ? 'it' : 'them'} to a city to unload
                </p>
              ) : null}
              <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                <SectionHead>
                  <span className="whitespace-nowrap">
                    Assigned here <span className="tabular-nums">{assigned}</span>
                  </span>
                </SectionHead>
                <div className="flex min-w-0 items-center justify-end gap-1.5">
                  {addReason ? (
                    <span id={reasonId} className="min-w-0 text-right text-fg-subtle">
                      {addReason}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    disabled={!addUnit}
                    aria-describedby={addReason ? reasonId : undefined}
                    title={
                      addUnit
                        ? 'Send the nearest idle worker of this city here; it stays pinned until released'
                        : (addReason ?? undefined)
                    }
                    onClick={() => addUnit && dispatch(assignIdleCommand(addUnit, building.id))}
                    className="btn btn-secondary h-6 shrink-0 px-2 text-xs"
                  >
                    + add idle worker
                  </button>
                </div>
              </div>
              {roster.length > 0 ? (
                <ul className="flex flex-wrap gap-1">
                  {roster.map((w) => (
                    <li
                      key={w.id}
                      title={`${w.glyph} ${w.number}${w.home ? ` of ${w.home}` : ''}: ${w.doing}${pinned.has(w.id) ? ' (pinned here)' : ''}`}
                      className={`chip gap-1 text-xs tabular-nums ${w.foreignHome ? 'text-warn' : ''}`}
                    >
                      <b className="font-medium text-fg">
                        {w.glyph} {w.number}
                      </b>
                      {pinned.has(w.id) ? <span title="Pinned here">📌</span> : null}
                      {w.home ? <span>{w.home}</span> : null}
                      <span className="text-fg-subtle">{w.doing}</span>
                      {pinned.has(w.id) ? (
                        <button
                          type="button"
                          aria-label={`Release ${w.glyph} ${w.number} back to auto`}
                          title="Release: back to auto"
                          onClick={() => dispatch(releaseCommand([w.id]))}
                          className="-my-1 -mr-1 cursor-pointer px-1.5 py-1 text-fg-subtle hover:text-fg"
                        >
                          ✕
                        </button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}
              <p className="text-fg-subtle">
                {roster.length === 0 ? 'No workers assigned · ' : ''}
                {tiles} {tiles === 1 ? 'tile' : 'tiles'} · never runs out
              </p>
            </>
          ) : null}
          {growth && !outside ? (
            <p className="text-fg-subtle">{growth}; it spreads over nearby land as it levels up</p>
          ) : null}
          {watch ? (
            <p className="text-fg-muted">
              Watches any tile up to {watch.range} tiles away and sees {watch.radius} around it
              {spotDistance === null
                ? ': no watch spot set'
                : spotDistance > watch.range
                  ? `: its spot, ${spotDistance} tiles away, is out of reach`
                  : `: watching ${spotDistance} tiles away`}
            </p>
          ) : null}
          {cityHp > 0 || cityStrength > 0 || cityVision > 0 ? (
            <p className="text-fg-muted">
              Its city gets{' '}
              {[
                cityHp > 0 ? `+${cityHp} hp` : null,
                cityStrength > 0 ? `+${cityStrength} strength` : null,
                cityVision > 0 ? `+${cityVision} sight` : null,
              ]
                .filter(Boolean)
                .join(' and ')}
            </p>
          ) : null}
          {capacity > 0 ? (
            <p className="text-fg-muted">
              {tiles} {tiles === 1 ? 'tile' : 'tiles'} · {storedCount}/{capacity} stored: units here
              are hidden and safe, and come out when ordered to move or when the city is attacked
            </p>
          ) : null}
          {unlocks.length > 0 ? (
            <p className="text-fg-subtle">Trains at its city: {unlocks.join(', ')}</p>
          ) : null}
          {effect ? (
            <p className="text-fg-muted">
              <b className="font-medium text-fg">Effect on {effect.city}</b>{' '}
              {rules.resources[effect.resource]?.name ?? effect.resource}{' '}
              <span className="tabular-nums text-ok">
                +{rateText(effect.resource, effect.total)}
              </span>{' '}
              from {effect.parts.length} {effect.parts.length === 1 ? 'building' : 'buildings'}
              {effect.parts.length > 1 ? (
                <span className="tabular-nums">
                  {' '}
                  ·{' '}
                  {effect.parts.map((p) => (
                    <span key={p.id} className={p.self ? 'text-fg' : undefined}>
                      {p.name} {Number(p.now.toFixed(2))}{' '}
                    </span>
                  ))}
                </span>
              ) : null}
            </p>
          ) : null}
        </>
      }
      applied={buildingApplied(state, rules, building, cityNumbers ?? new Map())}
      next={<LevelCard preview={levelPrev} onHighlight={onHighlight} />}
      footer={
        <>
          {watch && onSetWatch ? (
            <div className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate px-1 text-[11px] text-fg-subtle">
                {building.watch ? 'Pick a new spot, or stop watching' : 'Pick a tile to watch'}
              </span>
              {building.watch ? (
                <button
                  type="button"
                  onClick={() => dispatch({ type: 'watch', buildingId: building.id, target: null })}
                  className="btn btn-ghost shrink-0 text-xs"
                >
                  Clear
                </button>
              ) : null}
              <button
                type="button"
                title={`Pick a tile up to ${watch.range} tiles away (tinted): you see ${watch.radius} around it while the tower stands`}
                onClick={() => onSetWatch(building.id)}
                className="btn btn-secondary shrink-0 text-xs"
              >
                Set watch spot
              </button>
            </div>
          ) : null}
          <LevelAction
            preview={levelPrev}
            state={state}
            rules={rules}
            building={building}
            playerId={playerId}
            dispatch={dispatch}
          />
        </>
      }
    />
  );
}

/**
 * An own unfinished site: progress, whether a worker is building it, a
 * warning when it stands outside your territory (no worker will come to
 * build it), and Cancel, which refunds `pacing.cancelRefund` of its cost.
 */
function SitePanel({ state, building, playerId, rules, dispatch, onClose }: BuildingPanelProps) {
  const def = rules.buildings[building.type];
  const status = constructionStatus(state, rules, building);
  const outside = outsideTerritory(state, rules, building);
  const refund = cancelRefund(rules, building.type);
  const hasRefund = costParts(refund).length > 0;
  const cancellable = canCancel(state, rules, playerId, building.id);

  return (
    <DockPanel
      kind="building"
      title={`${def?.name ?? building.type} site`}
      chips={
        <>
          <span className="chip tabular-nums">
            {Math.floor(building.buildProgress * 100)}% built
          </span>
          <span className="chip tabular-nums">
            {Math.ceil(fromMilli(building.hp))}/
            {Math.ceil(fromMilli(buildingMaxHp(state, rules, building)))} hp
          </span>
        </>
      }
      onClose={onClose}
      footer={
        <div className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate px-1 text-[11px] text-fg-subtle">
            Cancel gives back {Math.round(rules.pacing.cancelRefund * 100)}% of its cost
          </span>
          <button
            type="button"
            disabled={!cancellable}
            onClick={() => dispatch({ type: 'cancel', buildingId: building.id })}
            className="btn btn-secondary shrink-0 text-xs"
          >
            Cancel site{' '}
            {hasRefund && (
              <>
                (+
                <Cost cost={refund} />)
              </>
            )}
          </button>
        </div>
      }
    >
      {outside ? (
        <p className="px-1 text-warn">
          Outside your territory: no worker will build it here. Cancel it to get part of its cost
          back.
        </p>
      ) : status?.secondsLeft != null ? (
        <p className="px-1 text-fg-muted">
          Being built · {formatDuration(status.secondsLeft)} left
        </p>
      ) : (
        <p className="px-1 text-fg-muted">
          Waiting for a worker: an automated worker of this city builds it, or move one onto it
        </p>
      )}
    </DockPanel>
  );
}
